import React from 'react';
import { CheckSquare, AlignJustify, PanelsTopLeft, DoorClosed, Home } from 'lucide-react';

/**
 * Компонент для ввода дополнительных параметров помещения
 * (плинтус, окна, двери, балкон)
 */
const AdditionalFeaturesSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  measurementUnits
}) => {
  const unit = measurementUnits.find(u => u.id === formData.measurementUnit)?.acronym || 'м';
  
  return (
    <div className="mb-6 bg-fuchsia-50 dark:bg-fuchsia-900/20 p-4 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <CheckSquare className="w-5 h-5 mr-2 text-fuchsia-500" />
        Дополнительные элементы
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Плинтус */}
        <div className="mb-3">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="includeSkirting"
              name="includeSkirting"
              checked={formData.includeSkirting}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeSkirting" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <AlignJustify className="w-4 h-4 mr-1 text-fuchsia-500" />
              Учитывать плинтус
            </label>
          </div>
          
          {formData.includeSkirting && (
            <div className="ml-6 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="skirtingHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Высота плинтуса ({unit})
                  </label>
                  <input
                    type="text"
                    id="skirtingHeight"
                    name="skirtingHeight"
                    value={formData.skirtingHeight}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.skirtingHeight ? 'input-error' : ''}`}
                    placeholder="Высота плинтуса"
                  />
                  {errors.skirtingHeight && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.skirtingHeight}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Окна */}
        <div className="mb-3">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="includeWindows"
              name="includeWindows"
              checked={formData.includeWindows}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeWindows" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <PanelsTopLeft className="w-4 h-4 mr-1 text-fuchsia-500" />
              Учитывать оконные проемы
            </label>
          </div>
          
          {formData.includeWindows && (
            <div className="ml-6 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="windowCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Количество окон
                  </label>
                  <input
                    type="text"
                    id="windowCount"
                    name="windowCount"
                    value={formData.windowCount}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.windowCount ? 'input-error' : ''}`}
                    placeholder="Количество"
                  />
                  {errors.windowCount && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.windowCount}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="windowHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Высота окна ({unit})
                  </label>
                  <input
                    type="text"
                    id="windowHeight"
                    name="windowHeight"
                    value={formData.windowHeight}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.windowHeight ? 'input-error' : ''}`}
                    placeholder={`Высота в ${unit}`}
                  />
                  {errors.windowHeight && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.windowHeight}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="windowWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ширина окна ({unit})
                  </label>
                  <input
                    type="text"
                    id="windowWidth"
                    name="windowWidth"
                    value={formData.windowWidth}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.windowWidth ? 'input-error' : ''}`}
                    placeholder={`Ширина в ${unit}`}
                  />
                  {errors.windowWidth && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.windowWidth}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Двери */}
        <div className="mb-3">
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="includeDoors"
              name="includeDoors"
              checked={formData.includeDoors}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeDoors" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <DoorClosed className="w-4 h-4 mr-1 text-fuchsia-500" />
              Учитывать дверные проемы
            </label>
          </div>
          
          {formData.includeDoors && (
            <div className="ml-6 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="doorCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Количество дверей
                  </label>
                  <input
                    type="text"
                    id="doorCount"
                    name="doorCount"
                    value={formData.doorCount}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.doorCount ? 'input-error' : ''}`}
                    placeholder="Количество"
                  />
                  {errors.doorCount && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.doorCount}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="doorHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Высота двери ({unit})
                  </label>
                  <input
                    type="text"
                    id="doorHeight"
                    name="doorHeight"
                    value={formData.doorHeight}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.doorHeight ? 'input-error' : ''}`}
                    placeholder={`Высота в ${unit}`}
                  />
                  {errors.doorHeight && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.doorHeight}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="doorWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ширина двери ({unit})
                  </label>
                  <input
                    type="text"
                    id="doorWidth"
                    name="doorWidth"
                    value={formData.doorWidth}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.doorWidth ? 'input-error' : ''}`}
                    placeholder={`Ширина в ${unit}`}
                  />
                  {errors.doorWidth && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.doorWidth}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Балкон */}
        <div>
          <div className="flex items-center mb-2">
            <input
              type="checkbox"
              id="includeBalconies"
              name="includeBalconies"
              checked={formData.includeBalconies}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeBalconies" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <Home className="w-4 h-4 mr-1 text-fuchsia-500" />
              Учитывать балкон/лоджию
            </label>
          </div>
          
          {formData.includeBalconies && (
            <div className="ml-6 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="balconyLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Длина балкона ({unit})
                  </label>
                  <input
                    type="text"
                    id="balconyLength"
                    name="balconyLength"
                    value={formData.balconyLength}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.balconyLength ? 'input-error' : ''}`}
                    placeholder={`Длина в ${unit}`}
                  />
                  {errors.balconyLength && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.balconyLength}
                    </p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="balconyWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ширина балкона ({unit})
                  </label>
                  <input
                    type="text"
                    id="balconyWidth"
                    name="balconyWidth"
                    value={formData.balconyWidth}
                    onChange={handleInputChange}
                    className={`form-input w-full ${errors.balconyWidth ? 'input-error' : ''}`}
                    placeholder={`Ширина в ${unit}`}
                  />
                  {errors.balconyWidth && (
                    <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                      {errors.balconyWidth}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          <span className="font-semibold">Подсказка:</span> Учет дверных и оконных проемов влияет на расчет площади стен, что важно 
          для точного определения количества материалов для отделки. Данные о балконе представлены отдельно в общем расчете площади.
        </p>
      </div>
    </div>
  );
};

export default AdditionalFeaturesSection;